'use client';

import useSubsequentEffect from '@carvana/merch-react-hooks/useSubsequentEffect';
import omit from 'lodash.omit';
import { useContext, useMemo, useRef } from 'react';
import { heliosSizes } from '~/constants/common/resizeBreakpoints';
import NextSelection from '~/constants/new-cars/NextSelection';
import SectionName from '~/constants/new-cars/SectionName';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type {
  SelectedDoors,
  TruckBodyStyleRequest
} from '~/contracts/new-cars';
import useDataDogRUM from '~/hooks/common/telemetry/useDataDogRUM';
import { useTrackClickEvents } from '~/hooks/common/telemetry/useTrackClickEvents';
import useTrackRendered from '~/hooks/common/telemetry/useTrackRendered';
import useResizeListener from '~/hooks/common/ui/useResizeListener';
import useGetCustomizeOptions from '~/hooks/new-cars/services/useGetCustomizeOptions';
import useGetNewCarConfiguration from '~/hooks/new-cars/services/useGetNewCarConfiguration';
import useRemoveQueryParams from '~/hooks/new-cars/useRemoveQueryParams';
import useTrackPerfectMatchViewed from '~/hooks/new-cars/useTrackPerfectMatchViewed';

type Props = {
  isFromSrp: boolean;
};

export default function ParentModelData({ isFromSrp }: Props) {
  useDataDogRUM();
  useRemoveQueryParams();
  useResizeListener(heliosSizes);
  useTrackClickEvents();
  useTrackPerfectMatchViewed();
  useTrackRendered('car-merch-ui-srp-ncc-lip2', isFromSrp);

  const {
    customizeOptions,
    isFirstSection,
    isSectionIncluded,
    modelVersions,
    selectedDoors,
    selectedModelVersion,
    selectedOptions,
    selectedTrim,
    selectedYear,
    setSelectedDoors,
    setSelectedModelVersion,
    setSelectedOptions,
    suppressNextDisplayFetchRef,
    trims,
    truckBodyStyles
  } = useContext(ParentModelContext);

  // Memoized so the reference stays stable while selectedTrim is unchanged,
  // preventing useGetNewCarConfiguration from re-firing on every render.
  const truckBodyStyleRequest = useMemo<TruckBodyStyleRequest | null>(
    () =>
      selectedTrim
        ? { ...selectedTrim, truckBodyStyle: selectedTrim.truckBodyStyle ?? '' }
        : null,
    [selectedTrim]
  );

  // When trim is the first section, cards show absolute price ("from $X") → sort by lowestPrice.
  // Otherwise, cards show a relative delta ("+$X") → sort by priceAdd.
  const trimSortKey = useMemo(
    () => (isFirstSection(SectionName.Trim) ? 'lowestPrice' : 'priceAdd'),
    [isFirstSection]
  );

  useGetNewCarConfiguration({
    loadableState: modelVersions,
    request: selectedYear,
    requestType: 'model versions'
  });

  useGetNewCarConfiguration({
    loadableState: trims,
    request: selectedDoors ?? selectedModelVersion,
    requestType: 'trims',
    sortKey: trimSortKey
  });

  useGetNewCarConfiguration({
    loadableState: truckBodyStyles,
    request: truckBodyStyleRequest,
    requestType: 'truck body styles',
    sortKey: 'lowestPrice'
  });

  useGetCustomizeOptions({
    loadableState: customizeOptions,
    request: selectedOptions,
    suppressNextRef: suppressNextDisplayFetchRef
  });

  // --- auto-select when a section is skipped (single result) ---
  useSubsequentEffect(() => {
    if (!selectedYear) return;

    const result = modelVersions.data.results[0];
    if (!modelVersions.isLoaded || !result) return;

    const isDoorData =
      modelVersions.data.nextSelection === NextSelection.DoorCount;
    const shouldAutoSelectDoors =
      isDoorData && !isSectionIncluded(SectionName.Doors) && !selectedDoors;
    const shouldAutoSelectModelVersion =
      !isDoorData &&
      !isSectionIncluded(SectionName.ModelVersion) &&
      !selectedModelVersion;

    if (shouldAutoSelectDoors) {
      setSelectedDoors(
        // Strip trim/truckBodyStyle: the API may propagate them into result.next
        // as forward-context hints, but they belong to a later step and would
        // cause the trims request to hit the TBS endpoint instead.
        omit(
          { ...result, ...result.next },
          'next',
          'trim',
          'truckBodyStyle'
        ) as SelectedDoors,
        { silent: true }
      );
    } else if (shouldAutoSelectModelVersion) {
      setSelectedModelVersion(
        omit({ ...result, ...result.next }, 'next', 'trim', 'truckBodyStyle'),
        { silent: true }
      );
    }
  }, [
    isSectionIncluded,
    modelVersions.data,
    modelVersions.isLoaded,
    selectedDoors,
    selectedModelVersion,
    selectedYear,
    setSelectedDoors,
    setSelectedModelVersion
  ]);

  useSubsequentEffect(() => {
    if (!truckBodyStyles.isLoaded) return;
    // Guard: when trim is cleared (Back / Start Over), truckBodyStyles.isLoaded
    // may still be true from the previous selection. Skip until trim is set.
    if (!selectedTrim) return;

    if (!truckBodyStyles.data.results[0]) {
      // No truck body style options for this trim — proceed directly to customize.
      setSelectedOptions({ ...selectedTrim });
    }
  }, [
    selectedTrim,
    setSelectedOptions,
    truckBodyStyles.data,
    truckBodyStyles.isLoaded
  ]);

  // --- Loadable state resets ---
  useSubsequentEffect(() => {
    modelVersions.reset.call(null);
  }, [modelVersions.reset, selectedYear]);

  // Keep isSectionIncluded in a ref so that only a real doors/modelVersion
  // selection change triggers a trim reset — not an unrelated sectionSequence
  // reference change (e.g. when truckBodyStyles.data resets and causes a new
  // sectionSequence array, which propagates to a new isSectionIncluded reference).
  const isSectionIncludedRef = useRef(isSectionIncluded);
  isSectionIncludedRef.current = isSectionIncluded;

  useSubsequentEffect(() => {
    const stepIsVisible =
      isSectionIncludedRef.current(SectionName.Doors) ||
      isSectionIncludedRef.current(SectionName.ModelVersion);
    const upstreamSelectionCleared = !selectedDoors && !selectedModelVersion;

    if (stepIsVisible || upstreamSelectionCleared) {
      trims.reset.call(null);
    }
  }, [selectedDoors, selectedModelVersion, trims.reset]);

  useSubsequentEffect(() => {
    truckBodyStyles.reset.call(null);
    // Only reset customize when clearing the trim (start over, back navigation),
    // not when switching between trims. Resetting on every trim change causes
    // customizeOptions.hasEverLoaded to go false, which drops activeSection back
    // to 'trim' mid-flow and produces a visible scroll flicker.
    if (!selectedTrim) {
      customizeOptions.reset.call(null);
    }
  }, [customizeOptions.reset, selectedTrim, truckBodyStyles.reset]);

  return null;
}
